import type { DocumentType, ObjectDescriptor } from '@qcbot/shared';
import { navigateToWordLocation } from './applyFixWord';

export type OfficeHost = 'powerpoint' | 'word' | 'unsupported';

/** Which Office app the taskpane is running in. Only valid after `Office.onReady`. */
export function getHost(): OfficeHost {
  switch (Office.context.host) {
    case Office.HostType.PowerPoint:
      return 'powerpoint';
    case Office.HostType.Word:
      return 'word';
    default:
      return 'unsupported';
  }
}

/** The file type QCBot is reviewing in this host. */
export function getDocumentType(): DocumentType {
  return getHost() === 'word' ? 'docx' : 'pptx';
}

/** Best-available "which file is this" signal, for the SCOPE CONFIRMATION rule and for uploads.
 * Word hands over a .docx whatever the open file is (.doc, .dotx…), so its extension is normalised. */
export function getCurrentFileName(): string {
  const host = getHost();
  const name = (Office.context.document.url?.split('/').pop() || (host === 'word' ? 'document.docx' : 'deck.pptx')).split('?')[0];
  return host === 'word' ? name.replace(/\.(docx|docm|dotx|dotm|doc|rtf)$/i, '') + '.docx' : name;
}

/** The open file's OneDrive/SharePoint link, or undefined for a local or unsaved file. Only a
 * cloud-hosted file can be co-authored, which run collaboration depends on. */
export function getCloudDocumentUrl(): string | undefined {
  const url = Office.context.document?.url;
  return url && /^https:\/\//i.test(url) ? url : undefined;
}

/** Stable hash of the cloud document's URL, so collaborators opening the same file can be matched
 * to the run. Undefined for local files, which have no shared identity. */
export async function getSourceDocumentKey(): Promise<string | undefined> {
  const url = getCloudDocumentUrl();
  if (!url) return undefined;
  let normalized = url.split(/[?#]/)[0];
  try {
    normalized = decodeURI(normalized);
  } catch {
    // Keep the raw URL when it isn't valid percent-encoding.
  }
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalized.toLowerCase()));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Reads the currently open file's raw bytes (.pptx or .docx) via the Office compressed-file API. */
export async function getActiveFileBytes(): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(Office.FileType.Compressed, { sliceSize: 4 * 1024 * 1024 }, (fileResult) => {
      if (fileResult.status !== Office.AsyncResultStatus.Succeeded) {
        reject(fileResult.error);
        return;
      }
      const file = fileResult.value;
      const sliceCount = file.sliceCount;
      const slices: Uint8Array[] = new Array(sliceCount);
      let received = 0;

      const getSlice = (index: number) => {
        file.getSliceAsync(index, (sliceResult) => {
          if (sliceResult.status !== Office.AsyncResultStatus.Succeeded) {
            file.closeAsync();
            reject(sliceResult.error);
            return;
          }
          slices[sliceResult.value.index] = new Uint8Array(sliceResult.value.data);
          received += 1;
          if (received === sliceCount) {
            file.closeAsync();
            const total = slices.reduce((sum, s) => sum + s.length, 0);
            const merged = new Uint8Array(total);
            let offset = 0;
            for (const slice of slices) {
              merged.set(slice, offset);
              offset += slice.length;
            }
            resolve(merged);
          } else {
            getSlice(index + 1);
          }
        });
      };

      if (sliceCount === 0) {
        file.closeAsync();
        resolve(new Uint8Array());
      } else {
        getSlice(0);
      }
    });
  });
}

/**
 * Navigates to and selects a shape on a slide, for click-to-navigate from the issue list.
 * Matches by shape *name* (PowerPoint.Shape.name), which corresponds to the OOXML
 * `<p:cNvPr name="...">` attribute we captured as `DeckShape.name`/`ObjectDescriptor.label`.
 * (Office.js's `Shape.id` is an opaque string, not the numeric OOXML shape id, so name is
 * the only reliable, documented bridge between the two. Also, PowerPoint.Shape/Slide have
 * no `.select()` method — selection goes through `Presentation.setSelectedSlides` and
 * `Slide.setSelectedShapes`, both of which take arrays of the opaque string ids.)
 */
export async function navigateToShape(slideNumber: number, shapeName?: string): Promise<void> {
  await PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load('items/id');
    await context.sync();
    const slide = slides.items[slideNumber - 1];
    if (!slide) return;

    context.presentation.setSelectedSlides([slide.id]);
    await context.sync();

    if (shapeName) {
      const target = await findShapeByNameRecursive(context, slide.shapes, shapeName);
      if (target) {
        slide.setSelectedShapes([target.id]);
        await context.sync();
      }
    }
  });
}

/** `slide.shapes` only lists TOP-LEVEL shapes — a shape nested inside a PowerPoint group (common
 * for header/footer/callout designs) isn't in that list, even though the backend's deck parser
 * flattens group children into its own shape list. Without this, click-to-navigate silently did
 * nothing for any grouped shape. */
async function findShapeByNameRecursive(
  context: PowerPoint.RequestContext,
  shapes: PowerPoint.ShapeCollection | PowerPoint.ShapeScopedCollection,
  shapeName: string,
): Promise<PowerPoint.Shape | undefined> {
  shapes.load('items/id,items/name,items/type');
  await context.sync();
  const direct = shapes.items.find((s) => s.name === shapeName);
  if (direct) return direct;
  for (const shape of shapes.items) {
    if (shape.type === PowerPoint.ShapeType.group) {
      const found = await findShapeByNameRecursive(context, shape.group.shapes, shapeName);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Click-to-navigate for an issue, in whichever host is open: PowerPoint jumps to the slide and
 * selects the shape by name; Word selects the paragraph (`locator`), or the first paragraph of the
 * page when the issue has no specific block (`unitAnchors`, from the run's progress).
 */
export async function navigateToTarget(target: Pick<ObjectDescriptor, 'slideNumber' | 'label' | 'locator'>, unitAnchors?: Record<string, number>): Promise<void> {
  if (getHost() === 'word') {
    const anchored = unitAnchors?.[String(target.slideNumber)];
    await navigateToWordLocation(target.locator ?? (anchored !== undefined ? { paragraphIndex: anchored } : undefined));
    return;
  }
  await navigateToShape(target.slideNumber, target.label);
}

